(function ($) {
    'use strict';

    /**
     * jQuery Plugin: glyphCopyToClipboard
     *
     * A plugin for copying text content to the clipboard with visual feedback using Bootstrap tooltips.
     * Supports multiple text sources (data attributes or innerHTML) and provides localized messages.
     * Ideal for copy-to-clipboard functionality on buttons, links, or any clickable elements.
     *
     * #### Example of usage
     * ```html
     * <!-- Basic usage with data attributes -->
     * <button class="copy-to-clipboard" data-full-length="Complete text to copy">
     *   <i class="bi bi-clipboard"></i> Copy Full Text
     * </button>
     *
     * <!-- Fallback to innerHTML -->
     * <span class="copy-to-clipboard">Short text to copy</span>
     *
     * <!-- With custom tooltip -->
     * <button class="copy-to-clipboard" data-bs-title="Click to copy ID" data-short-length="ABC123">
     *   Copy ID
     * </button>
     * ```
     *
     * ```javascript
     * // Auto-initialize (done automatically on document ready)
     * $('.copy-to-clipboard').glyphCopyToClipboard();
     *
     * // Custom configuration
     * $('.my-copy-buttons').glyphCopyToClipboard({
     *     tooltipDelay: 1000,
     *     tooltipSuccessClass: 'tooltip-green',
     *     fallbackMessages: {
     *         defaultTitle: 'Click to copy to clipboard',
     *         successTitle: 'Successfully copied!',
     *         errorTitle: 'Copy failed - try again'
     *     }
     * });
     * ```
     *
     * @param {Object} options - Configuration options for the plugin
     * @param {number} [options.tooltipDelay=500] - Delay in milliseconds before restoring original tooltip
     * @param {string} [options.tooltipSuccessClass='tooltip-success'] - CSS class applied to success tooltip
     * @param {Object} [options.fallbackMessages] - Fallback messages when localization is unavailable
     * @param {string} [options.fallbackMessages.defaultTitle='Click to copy!'] - Default tooltip text
     * @param {string} [options.fallbackMessages.successTitle='Copied to clipboard!'] - Success tooltip text
     * @param {string} [options.fallbackMessages.errorTitle='Failed to copy'] - Error tooltip text
     *
     * @returns {jQuery} The jQuery object for chaining
     */
    $.fn.glyphCopyToClipboard = function (options) {
        return initializeGlyphCopyToClipboard(this, options);
    };

    // Default configuration
    const DEFAULT_CONFIG = {
        tooltipDelay: 500,
        tooltipSuccessClass: 'tooltip-success',
        fallbackMessages: {
            defaultTitle: 'Click to copy!',
            successTitle: 'Copied to clipboard!',
            errorTitle: 'Failed to copy'
        }
    };

    /**
     * Retrieves messages with priority: user config > lang array > hard-coded defaults.
     * This allows users to override messages while still supporting dynamic language switching.
     *
     * @param {Object} config - Plugin configuration object
     * @param {Object} userConfig - Original user-provided configuration
     * @returns {Object} Object containing the appropriate messages
     */
    const getMessages = (config, userConfig = {}) => {
        // Priority: user-provided messages > lang array > hard-coded defaults
        const langDefaults = (typeof lang !== 'undefined' && lang) ? {
            defaultTitle: lang['copyToClipboardDefault'] || DEFAULT_CONFIG.fallbackMessages.defaultTitle,
            successTitle: lang['copyToClipboardSuccess'] || DEFAULT_CONFIG.fallbackMessages.successTitle,
            errorTitle: lang['copyToClipboardError'] || DEFAULT_CONFIG.fallbackMessages.errorTitle
        } : DEFAULT_CONFIG.fallbackMessages;

        if (userConfig.fallbackMessages) {
            return {
                defaultTitle: userConfig.fallbackMessages.defaultTitle || langDefaults.defaultTitle,
                successTitle: userConfig.fallbackMessages.successTitle || langDefaults.successTitle,
                errorTitle: userConfig.fallbackMessages.errorTitle || langDefaults.errorTitle
            };
        }
        return langDefaults;
    };

    /**
     * Extracts text content from an element based on priority:
     * 1. data-full-length attribute (highest priority)
     * 2. data-short-length attribute
     * 3. innerHTML content (lowest priority)
     *
     * @param {HTMLElement} element - The DOM element to extract text from
     * @returns {string} The extracted text content
     */
    const extractTextContent = (element) => {
        const $el = $(element);

        // Priority: data-full-length > data-short-length > innerHTML
        const fullLength = $el.data('full-length');
        const shortLength = $el.data('short-length');

        if (fullLength) return fullLength;
        if (shortLength) return shortLength;
        return $el.html();
    };

    /**
     * Gets existing Bootstrap tooltip instance or creates a new one if none exists.
     *
     * @param {HTMLElement} element - The DOM element to get/create tooltip for
     * @returns {bootstrap.Tooltip} Bootstrap tooltip instance
     */
    const getTooltipInstance = (element) => {
        let tooltip = bootstrap.Tooltip.getInstance(element);
        if (!tooltip) {
            tooltip = new bootstrap.Tooltip(element);
        }
        return tooltip;
    };

    /**
     * Service object for clipboard operations using the modern Clipboard API.
     * Provides asynchronous text copying with error handling.
     */
    const ClipboardService = {
        /**
         * Copies text to the clipboard using navigator.clipboard API.
         *
         * @param {string} text - The text to copy to clipboard
         * @returns {Promise<Object>} Promise resolving to {success: boolean, error?: Error}
         */
        copy: async (text) => {
            try {
                await navigator.clipboard.writeText(text);
                return { success: true };
            } catch (error) {
                console.error('Error copying text:', error);
                return { success: false, error };
            }
        }
    };

    /**
     * Service object for managing Bootstrap tooltip states and animations.
     * Handles tooltip creation, updating, showing, and restoration.
     */
    const TooltipService = {
        /**
         * Updates tooltip content using Bootstrap 5's setContent method for better performance.
         * Falls back to dispose/recreate if setContent is unavailable or custom class is needed.
         *
         * @param {HTMLElement} element - The element with the tooltip
         * @param {string} newTitle - New tooltip title text
         * @param {string|null} [customClass=null] - Optional custom CSS class for tooltip
         * @returns {bootstrap.Tooltip} The tooltip instance
         */
        updateAndShow: (element, newTitle, customClass = null) => {
            const $el = $(element);
            let tooltip = getTooltipInstance(element);

            // Update DOM attributes to keep them in sync
            $el.attr('data-bs-title', newTitle);
            if (customClass) {
                $el.attr('data-bs-custom-class', customClass);
            } else {
                $el.removeAttr('data-bs-custom-class');
            }

            // Try to use Bootstrap 5's setContent method for better performance
            if (tooltip && typeof tooltip.setContent === 'function' && !customClass) {
                try {
                    tooltip.setContent({ '.tooltip-inner': newTitle });
                    tooltip.show();
                    return tooltip;
                } catch (error) {
                    console.warn('Failed to update tooltip with setContent, falling back to recreation:', error);
                }
            }

            // Fallback: dispose and recreate (necessary for custom classes)
            if (tooltip) {
                tooltip.dispose();
            }

            // Create new tooltip with updated configuration
            tooltip = new bootstrap.Tooltip(element);
            tooltip.show();

            return tooltip;
        },

        /**
         * Restores the original tooltip title and removes custom styling after a delay.
         * Always uses dispose/recreate to ensure complete cleanup of both content and custom classes.
         *
         * @param {HTMLElement} element - The element with the tooltip
         * @param {string} originalTitle - Original tooltip title to restore
         * @param {Object} config - Plugin configuration object
         */
        restore: (element, originalTitle, config) => {
            const tooltip = getTooltipInstance(element);
            const $el = $(element);

            setTimeout(() => {
                // Update DOM attributes to keep them in sync
                $el.attr('data-bs-title', originalTitle);
                $el.removeAttr('data-bs-custom-class');

                // Always dispose and recreate to ensure complete cleanup of custom classes
                if (tooltip) {
                    tooltip.dispose();
                }

                // Recreate tooltip with original configuration
                const newTooltip = new bootstrap.Tooltip(element);
                newTooltip.hide();
            }, config.tooltipDelay);
        }
    };

    /**
     * Creates the main click event handler for copy-to-clipboard functionality.
     * This factory function allows for configuration injection and returns a reusable handler.
     *
     * @param {Object} config - Plugin configuration object
     * @param {Object} userConfig - Original user-provided configuration
     * @returns {Function} Async click event handler function
     */
    const createCopyHandler = (config, userConfig) => {
        /**
         * Async click event handler that performs the copy operation and manages tooltip feedback.
         *
         * @param {Event} e - The click event object
         * @returns {Promise<void>}
         */
        return async function (e) {
            e.preventDefault();

            const element = this;
            const $element = $(element);
            const textToCopy = extractTextContent(element);
            const messages = getMessages(config, userConfig); // Get messages dynamically on each click
            const originalTitle = $element.attr('data-bs-title') || messages.defaultTitle;

            // Ensure tooltip exists
            if (!$element.attr('data-bs-title')) {
                $element.attr('data-bs-title', originalTitle);
            }

            const result = await ClipboardService.copy(textToCopy);

            if (result.success) {
                TooltipService.updateAndShow(
                    element,
                    messages.successTitle,
                    config.tooltipSuccessClass
                );
                TooltipService.restore(element, originalTitle, config);
            } else {
                TooltipService.updateAndShow(element, messages.errorTitle);
                TooltipService.restore(element, originalTitle, config);
            }
        };
    };

    /**
     * Main initialization function that sets up the copy-to-clipboard functionality.
     * Configures tooltips, creates event handlers, and binds click events.
     *
     * @param {jQuery} elements - jQuery collection of elements to initialize
     * @param {Object} [userConfig={}] - User-provided configuration options
     * @returns {jQuery} The original jQuery object for chaining
     */
    const initializeGlyphCopyToClipboard = (elements, userConfig = {}) => {
        const config = $.extend(true, {}, DEFAULT_CONFIG, userConfig);
        const handler = createCopyHandler(config, userConfig);

        // Initialize tooltips for all elements
        elements.each(function () {
            const $this = $(this);
            const messages = getMessages(config, userConfig);

            // Set default title if not present
            if (!$this.attr('data-bs-title')) {
                $this.attr('data-bs-title', messages.defaultTitle);
            }

            // Initialize tooltip
            new bootstrap.Tooltip(this);
        });

        // Attach event handlers using namespaced events to avoid conflicts
        elements.off('click.glyphCopyClipboard').on('click.glyphCopyClipboard', handler);

        return elements;
    };

    // Auto-initialize on elements with copy-to-clipboard class when document is ready
    $(document).ready(function () {
        $('.copy-to-clipboard').glyphCopyToClipboard();
    });

})(jQuery);
